import React, { useState, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PhotoUploader = ({ photoType, propertyId, roomTypeId, onUploadSuccess, maxPhotos = 10 }) => {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );

    if (files.length > 0) {
      handleFiles(files);
    }
  }, []);

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = async (files) => {
    if (photos.length + files.length > maxPhotos) {
      toast.error(`Massimo ${maxPhotos} foto`);
      return;
    }

    setUploading(true);

    try {
      const token = localStorage.getItem('token');
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('photo_type', photoType);
        if (propertyId) formData.append('property_id', propertyId);
        if (roomTypeId) formData.append('room_type_id', roomTypeId);

        // Create preview
        const preview = URL.createObjectURL(file);

        try {
          const response = await axios.post(
            `${BACKEND_URL}/api/photos/upload`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          return {
            id: response.data.photo_id,
            preview,
            uploaded: true
          };
        } catch (error) {
          console.error('Upload error:', error);
          return { preview, uploaded: false, error: true };
        }
      });

      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter(r => r.uploaded);
      
      setPhotos(prev => [...prev, ...successfulUploads]);
      
      if (successfulUploads.length > 0) {
        toast.success(`${successfulUploads.length} foto caricate!`);
        if (onUploadSuccess) onUploadSuccess(successfulUploads);
      }
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (photoId, index) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPhotos(prev => prev.filter((_, i) => i !== index));
      toast.success('Foto rimossa');
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 transition-all ${
          dragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center justify-center gap-4 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            {uploading ? (
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            ) : (
              <Upload className="w-8 h-8 text-blue-600" />
            )}
          </div>
          <div>
            <p className="text-lg font-semibold text-slate-900 mb-1">
              Trascina qui le foto o clicca per selezionare
            </p>
            <p className="text-sm text-slate-600">
              Supportati: JPG, PNG, WebP (max {maxPhotos} foto)
            </p>
          </div>
          <label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileInput}
              className="hidden"
              disabled={uploading}
            />
            <Button
              type="button"
              variant="outline"
              className="cursor-pointer"
              disabled={uploading}
            >
              Seleziona File
            </Button>
          </label>
        </div>
      </div>

      {/* Photo Grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {photos.map((photo, index) => (
            <Card key={index} className="relative group overflow-hidden">
              <img
                src={photo.preview}
                alt={`Upload ${index + 1}`}
                className="w-full h-32 object-cover"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => removePhoto(photo.id, index)}
                  className="gap-2"
                >
                  <X className="w-4 h-4" />
                  Rimuovi
                </Button>
              </div>
              {photo.uploaded && (
                <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                  <Check className="w-4 h-4" />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoUploader;